import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/lib/i18n";

export default function NotificationsBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const load = useCallback(() => api.get("/notifications").then((r) => setData((prev) => {
    if (r.data.unread > prev.unread) refresh();
    return r.data;
  })).catch(() => {}), [refresh]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  const onOpen = async (v) => {
    setOpen(v);
    if (v && data.unread) {
      await api.post("/notifications/read");
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative h-10 w-10 grid place-items-center rounded-md border border-slate-200 hover:bg-slate-50">
          <Bell className="w-4 h-4" />
          {data.unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center">{data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b font-mono text-xs uppercase tracking-wider text-slate-500">{t("Notifications")}</div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="p-4 text-sm text-slate-500">{t("No notifications yet.")}</p>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => { setOpen(false); n.link && navigate(n.link); }} className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-slate-50 ${n.read ? "" : "bg-amber-50/60"}`}>
              <p className="text-sm font-semibold">{n.title}</p>
              <p className="text-xs text-slate-600 break-words">{n.body}</p>
              <p className="text-[10px] font-mono text-slate-400 mt-1">{fmtTime(n.created_at)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
